import React from 'react'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ErrorBoundary } from 'react-error-boundary'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'

import { Screensaver } from '@/components/ui/screensaver/Screensaver'
import { UpdateBanner } from '@/components/ui/update-banner/UpdateBanner'
import ErrorPage from '@/pages/ErrorPage'
import { CompanyProvider, ApiProvider, ConfigProvider } from '@/providers'
import { routes } from '@/routes'
import { isGeotabMode } from '@/services/geotabSession'

import './App.css'

console.log('App: %s', import.meta.env.VITE_NAME)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
})

const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <ConfigProvider>
        <QueryClientProvider client={queryClient}>
          <ApiProvider>
            <CompanyProvider>{children}</CompanyProvider>
          </ApiProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
        {/*
          The screensaver refreshes the app token through Auth0, so it can't run as a
          Geotab add-in. It isn't needed there either: GeotabAuthProvider silently
          re-authenticates from the MyGeotab session on a 401, and MyGeotab already
          enforces its own idle timeout around us.
        */}
        {!isGeotabMode() && <Screensaver />}
        {/* Inside ConfigProvider so it shares the app shell, but it needs no API or auth —
            it only polls the static host, so it works even when the session has lapsed. */}
        <UpdateBanner />
      </ConfigProvider>
    </ErrorBoundary>
  )
}

const router = createBrowserRouter(routes)

const WrappedApp: React.FC = () => (
  <AppProviders>
    <RouterProvider router={router} />
  </AppProviders>
)

export default WrappedApp
